import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, CheckCircle2, Circle, Plus, AlertTriangle, ExternalLink, Lock, Unlock, ListChecks, Paperclip, ArrowRightLeft, X, Ban, RotateCcw } from 'lucide-react';
import { useT } from '../../i18n';
import { api } from '../../api/client';
import { DropdownSelect } from '../../components/DropdownSelect';
import { ConfirmModal } from '../../components/ConfirmModal';
import { TaskDetailModal } from '../../components/TaskDetailModal';
import { LineTextarea } from '../../components/LineTextarea';
import { MigrateTaskModal } from '../../components/MigrateTaskModal';
import { CdeMilestoneLink } from '../../components/CdeMilestoneLink';
import { useRole } from '../../context/RoleContext';
import { useAuth } from '../../context/AuthContext';
import { getProjectMilestones } from '../../utils/milestoneProgress';
import { getDoneStatusIds, getClosedColumnIds } from '../../utils/stages';
import { projectDisplayCode } from '../../utils/identifiers';
import type { GridColumn, Project } from '../../types';
import { vjtApi, type ProjectTree, type TreeMilestone, type TreeTask } from './api';
import { TaskTodoList } from './TaskTodoList';
import { getProjectRole } from '../roles-projet/projectRoles';
import { DocumentsList } from '../documents/DocumentsList';
import { DocumentsRegister } from '../documents/DocumentsRegister';
import { milestoneHealth, HEALTH_TITLE_KEY, HEALTH_BADGE_CLASS } from '../../utils/executionHealth';
import { taskSaveErrorMessage } from '../../components/TaskMilestoneNotice';
import { fullDay, localYmd } from '../timeline-taches/dates';

// [Proposition: vue-jalons-taches] One page showing a project's four levels —
// project → milestones → tasks → to-do items — with who works on each
// milestone and the closing cascade: a task is marked Done by hand once its
// to-do list is fully checked, a milestone is closed once all its tasks are
// Done, and the project can go to Handover once every milestone is closed.

const NO_MILESTONE = '__none__';

// Labels of the manual PMO status, as on the project's Execution tab.

function ProgressBar({ done, total }: { done: number; total: number }) {
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <span className="vjt-progress" title={`${done}/${total}`}>
      <span className="vjt-progress-track"><span className="vjt-progress-fill" style={{ width: `${pct}%` }} /></span>
      <span className="vjt-progress-label">{done}/{total}</span>
    </span>
  );
}

// ── Level 3: one task row ──
// The title opens the task in place (TaskDetailModal); the "To-do" button
// folds its to-do list open. A private task the user can't open shows its
// title and details only.
function TaskRow({ task, expanded, onToggle, onOpen, onMarkDone, onReopen, onMigrate, onChanged, openColumns, onSetColumn }: {
  task: TreeTask;
  expanded: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onMarkDone: () => void;
  onReopen: () => void;
  /** "Migrer vers un autre projet" — opens the move dialog for this task. */
  onMigrate?: () => void; // only for the PMO (admins)
  onChanged: () => void;
  /** Columns a task can be moved to by hand here (To Do, In Progress, Blocked…) — Done has its own button. */
  openColumns: GridColumn[];
  onSetColumn: (columnId: number) => void;
}) {
  const { t } = useT();
  const checked = task.todos.filter(d => d.done).length;
  const open = task.todos.length - checked;
  const locked = !task.can_open;
  return (
    <div className={`vjt-task${task.is_done ? ' is-done' : ''}`} data-live-anchor={`task-${task.id}`}>
      <div className="vjt-task-row">
        {locked ? <span className="vjt-chevron-spacer" /> : (
          <button type="button" className="vjt-chevron" onClick={onToggle} title={t('vjt.todo.toggle')}>
            {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          </button>
        )}
        {task.is_done ? <CheckCircle2 size={14} className="vjt-ok" /> : <Circle size={14} className="vjt-muted" />}
        <button
          type="button"
          className={`vjt-task-title${locked ? ' is-locked' : ''}`}
          title={locked ? t('tasks.privateLocked') : t('vjt.task.openHere')}
          onClick={() => (locked ? alert(t('tasks.privateLocked')) : onOpen())}
        >
          {task.title}
        </button>
        {task.is_private && <span className="badge badge-warn" title={t('tasks.privateTaskHelp')}><Lock size={10} /> {t('tasks.privateTask')}</span>}
        {!locked && <a className="btn-icon" href={`#tasks?task=${task.id}`} title={t('vjt.task.open')}><ExternalLink size={12} /></a>}
        {!locked && onMigrate && <button type="button" className="btn-icon" title={t('tasks.migrateTask')} onClick={onMigrate}><ArrowRightLeft size={12} /></button>}
        {/* [Proposition: documents] The task has documents (listed when it's opened). */}
        {!locked && !!task.doc_count && (
          <button type="button" className="btn-icon vjt-todo-docs has-docs" title={t('docs.taskCount', { n: task.doc_count })} onClick={onOpen}>
            <Paperclip size={12} /><span>{task.doc_count}</span>
          </button>
        )}
        <span className="vjt-task-meta">
          {task.assignee_name || t('vjt.task.unassigned')}
          {!!task.collaborator_names?.length && <span title={t('collab.cardTitle', { names: task.collaborator_names.join(', ') })}> +{task.collaborator_names.length}</span>}
          {task.due_date && <> · {fullDay(task.due_date, t)}</>}
        </span>
        {/* To Do → In Progress (or Blocked) stays a manual decision, as with the board's
            drag-and-drop; Done goes through "Mark Done" (to-do list rule). */}
        {locked || task.is_done ? (
          <span className="badge">{task.column_label}</span>
        ) : (
          <select
            className="form-select vjt-status-select"
            value={task.column_id}
            title={t('vjt.task.statusHelp')}
            onChange={(e) => onSetColumn(Number(e.target.value))}
          >
            {openColumns.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        )}
        {!locked && (
          <>
            {/* Always visible, even with no items yet — it's how the list is opened. */}
            <button
              type="button"
              className={`btn btn-ghost btn-sm vjt-todo-toggle${expanded ? ' active' : ''}`}
              onClick={onToggle}
              title={t('vjt.todo.toggle')}
            >
              <ListChecks size={12} />
              {task.todos.length > 0 ? t('vjt.todo.count', { done: checked, total: task.todos.length }) : t('vjt.todo.addFirst')}
            </button>
            {/* A secondary action: the strong one is the green prompt below, shown
                once the to-do list is fully checked (it replaces this button then). */}
            {task.is_done ? (
              <button type="button" className="btn btn-ghost btn-sm" onClick={onReopen}>{t('vjt.task.reopen')}</button>
            ) : !(task.todos.length > 0 && open === 0) && (
              <button
                type="button"
                className="btn btn-ghost btn-sm vjt-done-btn"
                disabled={open > 0}
                title={open > 0 ? t('vjt.task.doneLocked', { n: open }) : t('vjt.task.doneHelp')}
                onClick={onMarkDone}
              >
                <CheckCircle2 size={12} /> {t('vjt.task.markDone')}
              </button>
            )}
          </>
        )}
      </div>
      {/* Done but with unchecked items (e.g. items added before this rule, or
          the task was closed elsewhere) — inconsistent, offer to reopen. */}
      {task.is_done && open > 0 && (
        <div className="vjt-warning vjt-warning--task">
          <AlertTriangle size={13} /> {t('vjt.task.doneWithOpenTodos', { n: open })}
          <button type="button" className="btn btn-ghost btn-sm" onClick={onReopen}>{t('vjt.task.reopen')}</button>
        </div>
      )}
      {/* Every to-do item is checked: offer to validate the task (still a human decision). */}
      {!locked && !task.is_done && task.todos.length > 0 && open === 0 && (
        <div className="vjt-prompt vjt-prompt--task">
          <CheckCircle2 size={14} />
          <span>{t('vjt.prompt.task')}</span>
          <button type="button" className="btn btn-accent btn-sm" onClick={onMarkDone}>{t('vjt.prompt.taskAction')}</button>
        </div>
      )}
      {expanded && <TaskTodoList taskId={task.id} todos={task.todos} onChanged={onChanged} collapsibleDescriptions taskDueDate={task.due_date} />}
    </div>
  );
}

// Cancelling a milestone removes it from the planning; the reason is required
// and goes to the project history as a comment. Open tasks are archived.
function CancelMilestoneModal({ name, openTasks, onClose, onConfirm }: {
  name: string;
  openTasks: number;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useT();
  const [reason, setReason] = useState('');
  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <h3 className="modal-title">{t('vjt.cancelMs.title', { name })}</h3>
          <button className="close-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">{t('vjt.cancelMs.reason')}</label>
            <textarea className="form-textarea" rows={3} autoFocus value={reason}
              placeholder={t('vjt.cancelMs.reasonPlaceholder')} onChange={(e) => setReason(e.target.value)} />
          </div>
          <p style={{ margin: 0, fontSize: 'var(--t-12)', color: 'var(--ink-3)', lineHeight: 1.5 }}>
            {openTasks > 0 ? t('vjt.cancelMs.warningTasks', { n: openTasks }) : t('vjt.cancelMs.warning')}
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-ghost" style={{ color: 'var(--err)' }} disabled={!reason.trim()} onClick={() => onConfirm(reason.trim())}>
            <Ban size={12} /> {t('vjt.cancelMs.confirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// A cancelled milestone stays in its place (the numbering doesn't shift),
// greyed: who cancelled it, when and why, its archived tasks on demand, and
// for the PMO or the sponsor a way to restore it.
function CancelledMilestoneBlock({ index, milestone, canRestore, onRestore }: {
  index: number;
  milestone: TreeMilestone;
  canRestore: boolean;
  onRestore: () => void;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const c = milestone.cancellation;
  const archived = milestone.archived_tasks;
  return (
    <div className="vjt-ms is-cancelled" data-live-anchor={`ms-${milestone.id}`}>
      <div className="vjt-ms-head">
        <button type="button" className="vjt-chevron" disabled={archived.length === 0} onClick={() => setOpen(v => !v)}
          title={t(open ? 'vjt.ms.fold' : 'vjt.ms.unfold')}>
          {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>
        <div className="vjt-ms-title">{t('vjt.ms.title', { n: index + 1, name: milestone.name || t('milestones.untitled') })}</div>
        <span className="badge badge-plain"><Ban size={11} /> {t('vjt.cancelled.badge')}</span>
        {canRestore && (
          <button type="button" className="btn btn-ghost btn-sm" title={t('vjt.cancelled.restoreHelp')} onClick={onRestore}>
            <RotateCcw size={12} /> {t('vjt.cancelled.restore')}
          </button>
        )}
      </div>
      <div className="vjt-ms-cancel-info">
        {c?.at ? t('vjt.cancelled.by', { date: fullDay(c.at.slice(0, 10), t), name: c.by || '—' }) : t('vjt.cancelled.badge')}
        {c?.reason && <> — {t('vjt.cancelled.reason', { reason: c.reason })}</>}
        {archived.length > 0 && (
          <> · <button type="button" className="vjt-ms-details-toggle" onClick={() => setOpen(v => !v)}>{t('vjt.cancelled.archivedTasks', { n: archived.length })}</button></>
        )}
      </div>
      {open && archived.length > 0 && (
        <ul className="vjt-ms-archived">
          {archived.map(task => <li key={task.id}>{task.title}</li>)}
        </ul>
      )}
    </div>
  );
}

// ── Level 2: one milestone and its tasks ──
// Folded or not, the header gives the summary — dates (in red once late),
// status, how many tasks are done, who works on it — so the milestone can be
// read without unfolding it. Unfolding adds the tasks (open ones first, by due
// date; done ones behind a toggle), the task form and the documents; the
// description and deliverables sit behind their own "details" toggle.
function MilestoneBlock({ projectId, index, milestone, tasks, team, canClose, cdeCanEdit, expandedTasks, onToggleTask, onOpenTask, onMarkDone, onReopenTask, onMigrateTask, onSetStatus, onCancel, onAddTask, onChanged, openColumns, onSetTaskColumn }: {
  projectId: number;
  index: number | null;
  milestone: TreeMilestone | null; // null → the "no milestone" group
  tasks: TreeTask[];
  team: ProjectTree['team'];
  canClose: boolean;
  cdeCanEdit: boolean; // may hand a process milestone to the CdE
  expandedTasks: Set<number>;
  onToggleTask: (id: number) => void;
  onOpenTask: (task: TreeTask) => void;
  onMarkDone: (task: TreeTask) => void;
  onReopenTask: (task: TreeTask) => void;
  onMigrateTask?: (task: TreeTask) => void; // only for the PMO (admins)
  onSetStatus: (status: 'completed' | 'active') => void;
  onCancel: () => void; // opens the cancel dialog (PMO or sponsor only)
  onAddTask: (title: string, plannedDate: string) => void;
  openColumns: GridColumn[];
  onSetTaskColumn: (task: TreeTask, columnId: number) => void;
  onChanged: () => void;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftPlanned, setDraftPlanned] = useState(''); // [Proposition: timeline-taches] required
  const doneCount = tasks.filter(x => x.is_done).length;
  const openCount = tasks.length - doneCount;
  const closed = milestone?.status === 'completed';
  const todayYMD = localYmd(new Date());
  const late = !!milestone && !closed && !!milestone.estimated_end && milestone.estimated_end < todayYMD;

  // Open tasks first, soonest due date first (none last); done ones apart.
  const openTasks = useMemo(() => tasks.filter(x => !x.is_done).sort((a, b) =>
    (a.due_date ?? '9999').localeCompare(b.due_date ?? '9999') || a.title.localeCompare(b.title)), [tasks]);
  const doneTasks = useMemo(() => tasks.filter(x => x.is_done), [tasks]);

  // Who works on this milestone: task assignees and collaborators (with their
  // open-task count) plus the people named on the milestone itself.
  const people = useMemo(() => {
    const map = new Map<string, number>();
    for (const task of tasks) {
      for (const raw of [task.assignee_name, ...(task.collaborator_names ?? [])]) {
        const name = raw?.trim();
        if (name) map.set(name, (map.get(name) ?? 0) + (task.is_done ? 0 : 1));
      }
    }
    for (const name of milestone?.assignments ?? []) if (!map.has(name)) map.set(name, 0);
    return [...map.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [tasks, milestone]);

  // [Proposition: roles-projet] Hovering a name shows the person's project role
  // with its précision, the role's explanation, then their open tasks.
  const personTitle = (name: string, n: number) => {
    const member = team.find(m => m.name.toLowerCase() === name.toLowerCase());
    const role = getProjectRole(member?.project_role);
    const precision = member?.precision ? ` — ${member.precision}` : '';
    const lines = role
      ? [`${t('roles.header')} : ${t(role.labelKey)}${precision}`, t(role.descKey)]
      : [`${t('roles.header')} : ${member ? t('roles.none') : t('vjt.ms.notOnTeam')}${precision}`];
    return [...lines, t('vjt.ms.personOpenTasks', { n })].join('\n');
  };

  const statusBadge = !milestone ? null
    : closed ? <span className="badge badge-accent">{t('vjt.ms.closed')}</span>
    : milestone.status === 'active' ? <span className="badge badge-ok">{t('vjt.ms.active')}</span>
    : <span className="badge badge-plain">{t('vjt.ms.pending')}</span>;

  const closeTitle = !canClose ? t('vjt.ms.closeNotAllowed')
    : openCount > 0 ? t('vjt.ms.closeLocked', { n: openCount })
    : t('vjt.ms.closeHelp');

  const hasDetails = !!milestone && (!!milestone.description.trim() || milestone.deliverables.length > 0);
  const msEnd = milestone?.estimated_end ?? null;

  const addTask = () => {
    const title = draft.trim();
    if (!title || !draftPlanned || !msEnd) return;
    setDraft('');
    setDraftPlanned('');
    onAddTask(title, draftPlanned);
  };

  const taskRow = (task: TreeTask) => (
    <TaskRow
      key={task.id}
      task={task}
      expanded={expandedTasks.has(task.id)}
      onToggle={() => onToggleTask(task.id)}
      onOpen={() => onOpenTask(task)}
      onMarkDone={() => onMarkDone(task)}
      onReopen={() => onReopenTask(task)}
      onMigrate={onMigrateTask && (() => onMigrateTask(task))}
      openColumns={openColumns}
      onSetColumn={(columnId) => onSetTaskColumn(task, columnId)}
      onChanged={onChanged}
    />
  );

  return (
    <div className={`vjt-ms${closed ? ' is-closed' : ''}${late ? ' is-late' : ''}`} data-live-anchor={milestone ? `ms-${milestone.id}` : 'ms-none'}>
      <div className="vjt-ms-head">
        <button type="button" className="vjt-chevron" onClick={() => setOpen(v => !v)} title={t(open ? 'vjt.ms.fold' : 'vjt.ms.unfold')}>
          {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>
        <div className="vjt-ms-title" onClick={() => setOpen(v => !v)}>
          {milestone ? t('vjt.ms.title', { n: index! + 1, name: milestone.name || t('milestones.untitled') }) : t('vjt.ms.none')}
        </div>
        {late && (
          <span className="badge badge-err" title={t('vjt.ms.lateHelp', { date: fullDay(msEnd!, t) })}>
            <AlertTriangle size={11} /> {t('vjt.ms.late')}
          </span>
        )}
        {milestone && (closed ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onSetStatus('active')}>
            <Unlock size={12} /> {t('vjt.ms.reopen')}
          </button>
        ) : (
          <button type="button" className="btn btn-ghost btn-sm" disabled={!canClose || openCount > 0} title={closeTitle} onClick={() => onSetStatus('completed')}>
            <Lock size={12} /> {t('vjt.ms.close')}
          </button>
        ))}
        {milestone && !closed && canClose && (
          <button type="button" className="btn btn-ghost btn-sm" title={t('vjt.cancelMs.help')} onClick={onCancel}>
            <Ban size={12} /> {t('vjt.cancelMs.action')}
          </button>
        )}
      </div>

      {/* The summary, folded or not. */}
      {milestone && (
        <div className="vjt-ms-summary">
          <span className={`vjt-ms-dates${late ? ' is-late' : ''}`}>
            {milestone.estimated_start ? fullDay(milestone.estimated_start, t) : '—'} → {msEnd ? fullDay(msEnd, t) : '—'}
          </span>
          {statusBadge}
          <span className="vjt-ms-progress">
            <ProgressBar done={doneCount} total={tasks.length} />
            <span>{t('vjt.ms.progressLabel')}</span>
          </span>
        </div>
      )}
      {people.length > 0 && (
        <div className="vjt-ms-people">
          <span className="vjt-muted">{t('vjt.ms.people')}</span>
          {people.map(([name, n]) => (
            <span key={name} className="vjt-person" title={personTitle(name, n)}>
              {name}{n > 0 && <span className="vjt-person-count">{n}</span>}
            </span>
          ))}
        </div>
      )}

      {/* Every task of the milestone is Done: offer to close it — shown folded or not.
          Only the PMO, the sponsor or the PMO accompanist can close. */}
      {milestone && !closed && tasks.length > 0 && openCount === 0 && (
        <div className="vjt-prompt">
          <CheckCircle2 size={14} />
          <span>{t('vjt.prompt.milestone')}</span>
          {canClose ? (
            <button type="button" className="btn btn-accent btn-sm" onClick={() => onSetStatus('completed')}>
              <Lock size={12} /> {t('vjt.prompt.milestoneAction')}
            </button>
          ) : (
            <span className="vjt-muted">{t('vjt.ms.closeNotAllowed')}</span>
          )}
        </div>
      )}

      {open && closed && openCount > 0 && (
        <div className="vjt-warning">
          <AlertTriangle size={13} /> {t('vjt.ms.closedWithOpenTasks', { n: openCount })}
        </div>
      )}

      {open && (
        <>
          {/* A process milestone: its CdE handoff and the delivery status the CdE returned. */}
          {milestone?.type === 'process' && <CdeMilestoneLink projectId={projectId} milestoneId={milestone.id} canEdit={cdeCanEdit} />}
          {/* Description and linked deliverables, on demand. */}
          {hasDetails && (
            <div className="vjt-ms-details">
              <button type="button" className="vjt-ms-details-toggle" onClick={() => setShowDetails(v => !v)}>
                {showDetails ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {t('vjt.ms.details')}
              </button>
              {showDetails && (
                <>
                  {milestone!.description.trim() && (
                    <div className="vjt-ms-desc">
                      <span className="vjt-muted vjt-ms-label">{t('vjt.ms.descriptionLabel')}</span>
                      {milestone!.description}
                    </div>
                  )}
                  {milestone!.deliverables.length > 0 && (
                    <div className="vjt-ms-deliverables">
                      <span className="vjt-muted">{t('vjt.ms.deliverables')}</span>
                      <ul>
                        {milestone!.deliverables.map(d => (
                          <li key={d.id}>
                            <span className="vjt-deliv-title">{d.title || t('milestones.untitled')}</span>
                            {d.body.trim() && <div className="vjt-deliv-body">{d.body}</div>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="vjt-ms-body">
            {tasks.length === 0 && <div className="vjt-muted">{t('vjt.ms.noTasks')}</div>}
            {openTasks.map(taskRow)}
            {doneTasks.length > 0 && (
              <>
                <button type="button" className="vjt-done-toggle" onClick={() => setShowDone(v => !v)}>
                  {showDone ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                  <CheckCircle2 size={13} className="vjt-ok" />
                  {t(doneTasks.length === 1 ? 'vjt.ms.doneTasksOne' : 'vjt.ms.doneTasks', { n: doneTasks.length })}
                  <span className="vjt-muted"> — {t(showDone ? 'vjt.ms.hide' : 'vjt.ms.show')}</span>
                </button>
                {showDone && doneTasks.map(taskRow)}
              </>
            )}

            {/* The quick add takes its due date from the milestone, so the "no milestone"
                group (titled as such) has none: such tasks are created from "New task". */}
            {milestone && (
              <div className="vjt-task-add">
                <LineTextarea
                  className="form-input--bare vjt-task-add-title"
                  value={draft}
                  placeholder={t('vjt.task.addPlaceholder')}
                  onChange={setDraft}
                  onKeyDown={(e) => { if (e.key === 'Enter') addTask(); }}
                />
                {/* [Proposition: timeline-taches] Planned date required; the due date is the milestone's end. */}
                <label className="vjt-task-add-date">
                  <span>{t('vjt.task.addPlannedLabel')}</span>
                  <input className="form-input" type="date" value={draftPlanned} onChange={(e) => setDraftPlanned(e.target.value)} />
                </label>
                <span className={`vjt-task-add-due${msEnd ? '' : ' is-missing'}`}>
                  {msEnd ? t('vjt.task.addDue', { date: fullDay(msEnd, t) }) : t('vjt.task.addNoDue')}
                </span>
                <button type="button" className="btn btn-ghost btn-sm" disabled={!draft.trim() || !draftPlanned || !msEnd} onClick={addTask}
                  title={!msEnd ? t('vjt.task.addNoDue') : !draftPlanned ? t('tlt.req.planned') : undefined}>
                  <Plus size={12} /> {t('vjt.task.add')}
                </button>
              </div>
            )}
            {/* [Proposition: documents] Files and links attached to the milestone itself. */}
            {milestone && (
              <div className="vjt-ms-docs">
                <DocumentsList
                  target={{ type: 'milestone', id: `${projectId}:${milestone.id}` }}
                  confirmTarget={t('vjt.ms.title', { n: index! + 1, name: milestone.name || t('milestones.untitled') })}
                  onChanged={() => onChanged()}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Level 1: the project ──
// On the proposals page, a project picker comes first. Given `projectId`
// (the project page's Execution tab), the view is fixed to that project and
// `onChanged` runs after every change made here, so the page can refresh what
// it shows of the project (milestone statuses, stage).
// reloadKey: bumped by the page when the project changed elsewhere (e.g. a milestone restored from the history).
// onAddMilestone: opens the Planning tab's milestone form (given by the project page to those who can edit the planning).
export function VueJalonsTaches({ projectId: fixedProjectId, onChanged, reloadKey, onAddMilestone }: { projectId?: number; onChanged?: () => void; reloadKey?: number; onAddMilestone?: () => void } = {}) {
  const { t } = useT();
  const { isAdmin, isDirection } = useRole();
  const cdeCanEdit = isAdmin || isDirection; // same rule as the project detail page's canEdit
  const [projects, setProjects] = useState<Project[]>([]);
  const [columns, setColumns] = useState<GridColumn[]>([]);
  const [projectId, setProjectId] = useState<number | ''>(fixedProjectId ?? '');
  const [tree, setTree] = useState<ProjectTree | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState<Set<number>>(new Set());
  const [pendingAdd, setPendingAdd] = useState<{ milestone: TreeMilestone; title: string; plannedDate: string } | null>(null);
  // Milestone being cancelled (CancelMilestoneModal), with its open-task count for the warning.
  const [cancelMs, setCancelMs] = useState<{ milestone: TreeMilestone; openTasks: number } | null>(null);
  // Cancelled milestone being restored (confirmation), from its greyed line.
  const [restoreMs, setRestoreMs] = useState<TreeMilestone | null>(null);
  // Task opened in place (TaskDetailModal), from its title.
  const [openTask, setOpenTask] = useState<TreeTask | null>(null);
  // Task being moved to another project (MigrateTaskModal), from its row's arrows icon.
  const [migrateTask, setMigrateTask] = useState<TreeTask | null>(null);
  // Only the PMO (admins) and this project's PMO accompanist move a task to another project.
  const { user } = useAuth();
  const canMigrate = isAdmin || (!!user && !!tree?.team.some(m => m.project_role === 'accompanist' && m.name.trim().toLowerCase() === user.name.trim().toLowerCase()));
  // The project stage "Handover" (delivering the project), and the confirmation before moving to it.
  const [handoverStatusId, setHandoverStatusId] = useState<number | null>(null);
  const [confirmHandover, setConfirmHandover] = useState(false);

  useEffect(() => {
    Promise.all([
      api.getProjects() as Promise<Project[]>,
      api.getKanbanColumns() as Promise<GridColumn[]>,
      api.getGridColumns('pipeline') as Promise<GridColumn[]>,
    ])
      .then(([ps, cols, stages]) => {
        const sorted = [...ps].sort((a, b) => a.name.localeCompare(b.name));
        setProjects(sorted);
        setColumns(cols);
        setHandoverStatusId(stages.find(s => s.label === 'Handover')?.id ?? null);
        if (fixedProjectId != null) return;
        const first = sorted.find(p => getProjectMilestones(p).length > 0) ?? sorted[0];
        if (first) setProjectId(first.id);
      })
      .catch((e: Error) => alert(e.message));
  }, [fixedProjectId]);
  useEffect(() => { if (fixedProjectId != null) setProjectId(fixedProjectId); }, [fixedProjectId]);

  const reload = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    vjtApi.getTree(projectId).then(setTree).catch((e: Error) => alert(e.message)).finally(() => setLoading(false));
  }, [projectId]);
  useEffect(() => { setTree(null); reload(); }, [reload]);
  useEffect(() => { if (reloadKey) reload(); }, [reloadKey]);

  const doneColumnId = useMemo(() => [...getDoneStatusIds(columns)][0], [columns]);
  // Reopened tasks go back to "In Progress" (or the first open column).
  const reopenColumnId = useMemo(() => {
    const done = getClosedColumnIds(columns);
    const openCols = [...columns].filter(c => !done.has(c.id)).sort((a, b) => a.sort_order - b.sort_order);
    return (openCols.find(c => /in progress|en cours/i.test(c.label)) ?? openCols[0])?.id;
  }, [columns]);
  // Every column but Done, in board order — what a task's status picker offers
  // (Archivées included, so a task can be archived from here too).
  const openColumns = useMemo(() => {
    const done = getDoneStatusIds(columns);
    return [...columns].filter(c => !done.has(c.id)).sort((a, b) => a.sort_order - b.sort_order);
  }, [columns]);
  const todoColumnId = useMemo(() => {
    const done = getClosedColumnIds(columns);
    return [...columns].filter(c => !done.has(c.id)).sort((a, b) => a.sort_order - b.sort_order)[0]?.id;
  }, [columns]);

  const run = (p: Promise<unknown>) => p.then(() => { reload(); onChanged?.(); }).catch((e: Error) => alert(taskSaveErrorMessage(e, t)));

  const toggleTask = (id: number) => setExpandedTasks(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const createTask = async (milestone: TreeMilestone | null, title: string, plannedDate: string) => {
    if (!tree || !todoColumnId) return;
    try {
      await api.createTask({ project_id: tree.project.id, column_id: todoColumnId, title, milestone_id: milestone?.id, planned_at: `${plannedDate}T00:00:00` });
    } catch (err: any) {
      throw new Error(taskSaveErrorMessage(err, t));
    }
    // Adding work to a closed milestone reopens it.
    if (milestone?.status === 'completed') await vjtApi.setMilestoneStatus(tree.project.id, milestone.id, 'active');
  };

  const onAddTask = (milestone: TreeMilestone | null, title: string, plannedDate: string) => {
    if (milestone?.status === 'completed') setPendingAdd({ milestone, title, plannedDate });
    else run(createTask(milestone, title, plannedDate));
  };

  const groups = useMemo(() => {
    if (!tree) return [];
    const known = new Set(tree.milestones.map(m => m.id));
    const byMs = tree.milestones.map((m, i) => ({ index: i, milestone: m as TreeMilestone | null, tasks: tree.tasks.filter(x => x.milestone_id === m.id) }));
    const orphan = tree.tasks.filter(x => !x.milestone_id || !known.has(x.milestone_id));
    return [...byMs, { index: null, milestone: null, tasks: orphan }];
  }, [tree]);

  // Cancelled milestones count nowhere: not in the progress, not in what handover waits for.
  const activeMilestones = tree?.milestones.filter(m => m.status !== 'cancelled') ?? [];
  const closedCount = activeMilestones.filter(m => m.status === 'completed').length;
  const msCount = activeMilestones.length;
  const handoverReady = msCount > 0 && closedCount === msCount;

  // Execution-tab status: "at risk" once a milestone that isn't closed is past
  // its estimated end (same rule as ProjectDetailPage's overdueOpenMilestone).
  const todayYMD = new Date().toISOString().slice(0, 10);
  const lateMilestones = (tree?.milestones ?? [])
    .map((m, i) => ({ m, i }))
    .filter(({ m }) => m.status !== 'completed' && m.status !== 'cancelled' && !!m.estimated_end && m.estimated_end < todayYMD)
    .map(({ m, i }) => t('vjt.ms.title', { n: i + 1, name: m.name || t('milestones.untitled') }));
  const health = tree ? milestoneHealth({ milestones: tree.milestones }) : null;

  return (
    <div className="vjt">
      {fixedProjectId == null && (
        <div className="toolbar mb-16">
          <DropdownSelect
            value={projectId}
            options={projects.map(p => ({ value: p.id, label: `${projectDisplayCode(p).toUpperCase()} — ${p.name}` }))}
            placeholder={t('vjt.pickProject')}
            onChange={(v) => setProjectId(v === '' ? '' : Number(v))}
            triggerStyle={{ width: 360 }}
          />
        </div>
      )}

      {!tree ? (
        loading ? <div className="spinner" /> : null
      ) : (
        <>
          {/* Inside the project page (fixed project), the page already shows the
              name, the status, the milestone count and the Transfert action
              (stage bar): the wrapper stays empty there (hidden by CSS). */}
          <div className={fixedProjectId ? 'vjt-embedded-summary' : 'detail-card'}>
            {!fixedProjectId && <div className="detail-card-header">
              <h2 className="detail-card-title">{tree.project.name}</h2>
              <div className="detail-card-header-tags">
                <span className="badge">{projectDisplayCode(tree.project).toUpperCase()}</span>
                {tree.project.status_label && <span className="badge">{tree.project.status_label}</span>}
              </div>
            </div>}
            {/* The project's status from its milestones (late, ending soon, on time). On
                the project page it is in the masthead, so only the standalone view shows it. */}
            {!fixedProjectId && health && (
              <div className="vjt-project-summary mb-8">
                <span>{t('projectDetail.execution.statusLabel')}</span>
                <span className={`badge ${HEALTH_BADGE_CLASS[health.level]}`}>{t(HEALTH_TITLE_KEY[health.level])}</span>
                {lateMilestones.length > 0 && (
                  <span className="vjt-muted">{t('vjt.project.lateMilestones', { names: lateMilestones.join(', ') })}</span>
                )}
              </div>
            )}
            {!fixedProjectId && <div className="vjt-project-summary">
              <span>{t('vjt.project.milestonesClosed')}</span>
              <ProgressBar done={closedCount} total={msCount} />
              {handoverReady
                ? <span className="badge badge-ok">{t('vjt.project.handoverReady')}</span>
                : <span className="vjt-muted">{t('vjt.project.handoverLocked')}</span>}
            </div>}
            {/* Every milestone is closed: offer to deliver the project (move it to the Handover stage). */}
            {!fixedProjectId && handoverReady && handoverStatusId != null && !['Handover', 'Closed'].includes(tree.project.status_label ?? '') && (
              <div className="vjt-prompt vjt-prompt--project">
                <CheckCircle2 size={14} />
                <span>{t('vjt.prompt.project')}</span>
                <button type="button" className="btn btn-accent btn-sm" onClick={() => setConfirmHandover(true)}>
                  {t('vjt.prompt.projectAction')}
                </button>
              </div>
            )}
          </div>

          {msCount === 0 && <div className="vjt-muted mb-16">{t('vjt.project.noMilestones')}</div>}

          {groups.map(g => g.milestone?.status === 'cancelled' ? (
            <CancelledMilestoneBlock
              key={g.milestone.id}
              index={g.index!}
              milestone={g.milestone}
              canRestore={tree.canCloseMilestones}
              onRestore={() => setRestoreMs(g.milestone)}
            />
          ) : (g.milestone || g.tasks.length > 0) && (
            <MilestoneBlock
              key={g.milestone?.id ?? NO_MILESTONE}
              projectId={tree.project.id}
              index={g.index}
              milestone={g.milestone}
              tasks={g.tasks}
              team={tree.team ?? []}
              canClose={tree.canCloseMilestones}
              cdeCanEdit={cdeCanEdit}
              expandedTasks={expandedTasks}
              onToggleTask={toggleTask}
              onOpenTask={setOpenTask}
              onMarkDone={(task) => doneColumnId && run(api.moveTask(task.id, doneColumnId))}
              onReopenTask={(task) => reopenColumnId && run(api.moveTask(task.id, reopenColumnId))}
              onMigrateTask={canMigrate ? setMigrateTask : undefined}
              onSetStatus={(status) => g.milestone && run(vjtApi.setMilestoneStatus(tree.project.id, g.milestone.id, status))}
              onCancel={() => g.milestone && setCancelMs({ milestone: g.milestone, openTasks: g.tasks.filter(x => !x.is_done).length })}
              onAddTask={(title, plannedDate) => onAddTask(g.milestone, title, plannedDate)}
              openColumns={openColumns}
              onSetTaskColumn={(task, columnId) => columnId !== task.column_id && run(api.moveTask(task.id, columnId))}
              onChanged={reload}
            />
          ))}

          {/* Below the last milestone, as on the Planning tab. */}
          {onAddMilestone && (
            <button type="button" className="btn btn-ghost btn-sm" style={{ marginBottom: 'var(--s16)' }} onClick={onAddMilestone}>
              <Plus className="icon-sm" /> {t('milestones.addMilestone')}
            </button>
          )}

          {/* [Proposition: documents] Every document of the project, with what it's attached to. */}
          <DocumentsRegister projectId={tree.project.id} milestones={tree.milestones} tasks={tree.tasks} reloadKey={tree} />
        </>
      )}

      {openTask && tree && (
        <TaskDetailModal
          task={{
            id: openTask.id,
            source: 'project',
            title: openTask.title,
            description: openTask.description,
            due_date: openTask.due_date,
            planned_at: openTask.planned_at,
            planned_minutes: openTask.planned_minutes ?? 60,
            project_id: tree.project.id,
            project_code: tree.project.project_code,
            project_title: tree.project.name,
            board_id: null,
            board_name: null,
            assignee_name: openTask.assignee_name,
            column_key: null,
          }}
          contextProjectId={tree.project.id}
          projects={projects}
          onClose={() => { setOpenTask(null); reload(); }}
          onSaved={() => { setOpenTask(null); reload(); }}
        />
      )}
      {migrateTask && tree && (
        <MigrateTaskModal
          taskId={migrateTask.id}
          fromProjectId={tree.project.id}
          projects={projects}
          onClose={() => setMigrateTask(null)}
          onMigrated={() => { setMigrateTask(null); reload(); onChanged?.(); }}
        />
      )}
      {cancelMs && tree && (
        <CancelMilestoneModal
          name={cancelMs.milestone.name || t('milestones.untitled')}
          openTasks={cancelMs.openTasks}
          onClose={() => setCancelMs(null)}
          onConfirm={(reason) => {
            const ms = cancelMs.milestone;
            setCancelMs(null);
            run(vjtApi.cancelMilestone(tree.project.id, ms.id, reason));
          }}
        />
      )}
      <ConfirmModal
        open={restoreMs != null}
        title={t('timeline.restoreMilestoneTitle')}
        message={restoreMs?.archived_tasks.length
          ? t('timeline.restoreMilestoneMessageTasks', { name: restoreMs.name || t('milestones.untitled'), n: restoreMs.archived_tasks.length })
          : t('timeline.restoreMilestoneMessage', { name: restoreMs?.name || t('milestones.untitled') })}
        confirmLabel={t('timeline.restoreMilestone')}
        onConfirm={() => {
          const ms = restoreMs;
          setRestoreMs(null);
          if (ms && tree) run(vjtApi.restoreMilestone(tree.project.id, ms.id));
        }}
        onCancel={() => setRestoreMs(null)}
      />
      <ConfirmModal
        open={pendingAdd != null}
        title={t('vjt.reopenConfirm.title')}
        message={t('vjt.reopenConfirm.message', { name: pendingAdd?.milestone.name || t('milestones.untitled') })}
        confirmLabel={t('vjt.reopenConfirm.confirm')}
        onConfirm={() => { const p = pendingAdd; setPendingAdd(null); if (p) run(createTask(p.milestone, p.title, p.plannedDate)); }}
        onCancel={() => setPendingAdd(null)}
      />
      <ConfirmModal
        open={confirmHandover}
        title={t('vjt.prompt.projectConfirmTitle')}
        message={t('vjt.prompt.projectConfirmMessage', { name: tree?.project.name ?? '' })}
        confirmLabel={t('vjt.prompt.projectAction')}
        onConfirm={() => {
          setConfirmHandover(false);
          if (tree && handoverStatusId != null) run(api.updateProjectStatus(tree.project.id, handoverStatusId));
        }}
        onCancel={() => setConfirmHandover(false)}
      />
    </div>
  );
}
